import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, Flag } from '@mui/icons-material'

import {
    useCreateGoalMutation,
    useDeleteGoalMutation,
    useGetGoalsReportQuery,
    useListGoalsQuery,
} from '@/apis/v2Api'
import { cleanMatcher, matcherLabel, v2ErrorMessage } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import { ReportHeader, ReportStatus, fmtNum, fmtPct } from './common'
import { EMPTY_MATCHER, EVENT_NAME_RE, MatcherFields, type MatcherInput } from './MatcherFields'

/** Goal definitions (v2 `/goals`) with results from `/reports/goals`. */
export function GoalsPage() {
    const [period, setPeriod] = useState<Period>('30d')
    const args = useReportArgs(period)
    const defs = useListGoalsQuery(args.projectId)
    const report = useGetGoalsReportQuery(args, { skip: (defs.data ?? []).length === 0 })
    const [createGoal, createState] = useCreateGoalMutation()
    const [deleteGoal] = useDeleteGoalMutation()

    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [match, setMatch] = useState<MatcherInput>(EMPTY_MATCHER)
    const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null)

    const stats = new Map((report.data?.goals ?? []).map(g => [g.id, g]))
    const canSave = name.trim() !== '' && EVENT_NAME_RE.test(match.event.trim())

    const save = async () => {
        try {
            await createGoal({
                projectId: args.projectId,
                name: name.trim(),
                match: cleanMatcher(match),
            }).unwrap()
            setOpen(false)
            setName('')
            setMatch(EMPTY_MATCHER)
        } catch {
            // shown from createState.error
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Goals"
                subtitle="A goal counts events that match a rule; conversion rate is converting sessions / all sessions."
                period={period}
                onPeriodChange={setPeriod}
                actions={
                    <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
                        New goal
                    </Button>
                }
            />
            <ReportStatus isLoading={defs.isLoading} error={defs.error}>
                {(defs.data ?? []).length === 0 ? (
                    <EmptyState
                        icon={<Flag />}
                        title="No goals yet"
                        description="Track sign-ups, leads, downloads or visits to a key page as goals."
                        actionLabel="Create goal"
                        onAction={() => setOpen(true)}
                    />
                ) : (
                    <Card>
                        <CardContent>
                            {report.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {v2ErrorMessage(report.error)}
                                </Alert>
                            )}
                            {(defs.data ?? []).length > 12 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Results are computed for the first 12 goals.
                                </Alert>
                            )}
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Goal</TableCell>
                                        <TableCell>Rule</TableCell>
                                        <TableCell align="right">Conversions</TableCell>
                                        <TableCell align="right">Sessions</TableCell>
                                        <TableCell align="right">Visitors</TableCell>
                                        <TableCell align="right">Value</TableCell>
                                        <TableCell align="right">Conv. rate</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(defs.data ?? []).map(g => {
                                        const s = stats.get(g.id)
                                        return (
                                            <TableRow key={g.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {g.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontFamily: 'monospace' }}
                                                    >
                                                        {matcherLabel(g.match)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {report.isFetching
                                                        ? '…'
                                                        : fmtNum(s?.conversions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(s?.converting_sessions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(s?.converting_visitors)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(s?.value, 2)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtPct(s?.conversion_rate)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Delete goal">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setToDelete({
                                                                    id: g.id,
                                                                    name: g.name,
                                                                })
                                                            }
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </ReportStatus>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New goal</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {createState.error && (
                        <Alert severity="error">{v2ErrorMessage(createState.error)}</Alert>
                    )}
                    <TextField
                        size="small"
                        label="Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={{ mt: 1 }}
                        required
                    />
                    <MatcherFields value={match} onChange={setMatch} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!canSave || createState.isLoading}
                        onClick={() => {
                            void save()
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!toDelete}
                title="Delete goal?"
                message={`"${toDelete?.name ?? ''}" will be removed. Event data is not affected.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deleteGoal({ projectId: args.projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </Box>
    )
}
